<script setup lang="ts">
import { computed, onBeforeUnmount, ref, useId, useTemplateRef, watch } from 'vue'
import { autoUpdate, flip, offset, type Placement, shift, useFloating } from '@floating-ui/vue'

import { overlayLayerTarget } from '@/utils/overlayLayers'
import { vSafeHtml } from '@ui/utils/safeHtml'

import AppIcon from '@ui/app-icon/AppIcon.vue'

import type { AppTooltipProps } from './types'

const props = withDefaults(defineProps<AppTooltipProps>(), {
  position: 'top',
  maxWidth: 280,
  strategy: 'fixed',
  title: '',
  text: '',
  disabled: false,
  teleport: true,
})

const CLOSE_DELAY = 120

const tooltipId = useId()
const floatingTarget = overlayLayerTarget('dropdowns')
const isOpen = ref(false)

const maxWidth = computed(() =>
  typeof props.maxWidth === 'number' ? `${props.maxWidth}px` : props.maxWidth,
)
const placement = computed<Placement>(() => props.position)
const strategy = computed(() => props.strategy)

const triggerRef = useTemplateRef<HTMLElement>('triggerRef')
const floatingRef = useTemplateRef<HTMLElement>('floatingRef')

const { floatingStyles } = useFloating(triggerRef, floatingRef, {
  placement,
  strategy,
  middleware: [
    flip(),
    shift(),
    offset(8),
  ],
  whileElementsMounted: autoUpdate,
  transform: false,
})

let closeTimer: ReturnType<typeof setTimeout> | null = null
let isRestoringFocus = false

const cancelClose = () => {
  clearTimeout(closeTimer ?? undefined)
  closeTimer = null
}

const open = () => {
  if (props.disabled) return
  cancelClose()
  isOpen.value = true
}

const close = () => {
  cancelClose()
  isOpen.value = false
}

const scheduleClose = () => {
  cancelClose()
  closeTimer = setTimeout(close, CLOSE_DELAY)
}

const isInside = (node: Node | null) => Boolean(node) &&
  Boolean(triggerRef.value?.contains(node) || floatingRef.value?.contains(node))

const onFocusIn = () => {
  if (isRestoringFocus) {
    isRestoringFocus = false
    return
  }

  open()
}

const onFocusOut = (event: FocusEvent) => {
  if (isInside(event.relatedTarget as Node | null)) return
  close()
}

const restoreTriggerFocus = () => {
  const focusTarget = triggerRef.value?.querySelector<HTMLElement>('[tabindex]')
  if (!focusTarget) return
  isRestoringFocus = true
  focusTarget.focus()
}

const onDocumentEscape = (event: KeyboardEvent) => {
  if (event.key !== 'Escape') return
  const isFromContent = Boolean(floatingRef.value?.contains(event.target as Node | null))
  event.preventDefault()
  event.stopPropagation()
  close()
  if (isFromContent) restoreTriggerFocus()
}

watch(isOpen, (value) => {
  if (value) document.addEventListener('keydown', onDocumentEscape, true)
  else document.removeEventListener('keydown', onDocumentEscape, true)
})

onBeforeUnmount(() => {
  cancelClose()
  document.removeEventListener('keydown', onDocumentEscape, true)
})

let isOpenOnPointerDown: boolean | null = null

const rememberPointerDownState = () => {
  isOpenOnPointerDown = isOpen.value
}

const toggle = () => {
  if (props.disabled) return
  cancelClose()
  const wasOpen = isOpenOnPointerDown ?? isOpen.value
  isOpenOnPointerDown = null
  isOpen.value = !wasOpen
}

// Spread onto the real focusable trigger element (e.g. the slotted button) so it
// becomes the accessible, described trigger — exposed via the `trigger` slot's `bind`.
const triggerBind = computed(() => ({
  tabindex: '0',
  'aria-describedby': isOpen.value ? tooltipId : undefined,
}))
</script>

<template>
  <div class="app-tooltip">
    <div
      ref="triggerRef"
      data-test-id="app-tooltip-trigger"
      @mouseenter="open"
      @mouseleave="scheduleClose"
      @pointerdown="rememberPointerDownState"
      @click="toggle"
      @focusin="onFocusIn"
      @focusout="onFocusOut"
    >
      <slot name="trigger" :bind="triggerBind">
        <div
          class="app-tooltip-trigger"
          v-bind="triggerBind"
          data-test-id="app-tooltip-default-trigger"
        >
          <app-icon
            class="app-tooltip-icon"
            name="InfoCircle"
          />
        </div>
      </slot>
    </div>

    <teleport
      :to="floatingTarget"
      :disabled="!teleport"
    >
      <transition name="slide-to-top">
        <div
          v-if="isOpen"
          :id="tooltipId"
          ref="floatingRef"
          role="tooltip"
          class="app-tooltip-content"
          :class="{ inline: !teleport }"
          :style="[floatingStyles, { maxWidth }]"
          data-test-id="app-tooltip-content"
          @mouseenter="cancelClose"
          @mouseleave="scheduleClose"
          @focusout="onFocusOut"
        >
          <div
            v-if="$slots.title"
            class="app-tooltip-title"
            data-test-id="app-tooltip-title"
          >
            <slot name="title" />
          </div>
          <div
            v-else-if="title"
            v-safe-html="title"
            class="app-tooltip-title"
            data-test-id="app-tooltip-title"
          />
          <slot name="body">
            <span v-safe-html="text" />
          </slot>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<style lang="scss" scoped>
.app-tooltip {
  display: inline-block;
  max-width: 100%;
  vertical-align: middle;

  &-icon {
    color: inherit;
  }

  &-content {
    z-index: var(--z-layer-floating);
    width: max-content;
    padding: 8px 12px;
    border-radius: 8px;
    background-color: var(--g-neutral-1);
    box-shadow: var(--drop-down-shadow);
    color: var(--g-neutral-8);
    font-family: "General Sans", sans-serif;
    overflow-wrap: anywhere;

    &.inline {
      z-index: var(--z-page-popover);
    }
  }

  &-title {
    margin-bottom: 2px;
    font-weight: 600;
    text-align: left;
  }
}
</style>
